import Image from 'next/image';
import React, { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import squareLogo from '../../public/square.svg';
import { createOrder } from '../../services/order';
import countries from '../../utils/countries';
import { formatCurrency } from '../../utils/currency';
import request from '../../utils/request';
import { darkModeCardStyle, tokenize, verifyBuyer } from '../../utils/square';
import Notification from '../Notification';
import Select from '../Select';
import TextInput from '../TextInput';
import Textarea from '../Textarea';

type Ticket = {
  ID: number;
  Name: string;
  Price: number;
  Description: string;
  Available: number;
};

type Order = {
  [key: string]: {
    unitPrice: number;
    quantity: number;
    total: number;
  };
};

interface Props {
  baseId: string;
  date: string;
}

const TicketBooker: React.FC<Props> = ({ baseId, date }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [isSquareInitialized, setIsSquareInitialized] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string>('');
  const [order, setOrder] = useState<Order>({});
  const [isOrderConfirmed, setIsOrderConfirmed] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      teamName: '',
      dietaryRequirements: '',
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      addressLine1: '',
      addressLine2: '',
      county: '',
      city: '',
      country: 'GB'
    }
  });

  const [card, setCard] = useState(null);

  const hasOrder = Object.keys(order).length;

  let payments = useRef(null);

  useEffect(() => {
    const fetchTicketData = async () => {
      setIsLoading(true);
      const {
        data: { tickets }
      } = (await request(`/api/event/${baseId}`)) as { data: { tickets: Ticket[] } };
      setTickets(tickets);
      setIsLoading(false);
    };

    fetchTicketData();
  }, [baseId]);

  useEffect(() => {
    if (hasOrder) {
      if (!(window as any).Square) {
        throw new Error('Square.js failed to load properly');
      }

      const initializeSquare = async () => {
        payments.current = (window as any).Square.payments(
          process.env.NEXT_PUBLIC_SQUARE_APPLICATION_ID,
          process.env.NEXT_PUBLIC_SQUARE_LOCATION_ID
        );

        try {
          setCard(await initializeCard(payments.current));
          setIsSquareInitialized(true);
        } catch (e) {
          console.error('Initializing Card failed', e);
          return;
        }
      };

      initializeSquare();
    }
  }, [hasOrder]);

  const initializeCard = async (payments: any) => {
    const card = await payments.card({
      style: darkModeCardStyle
    });
    await card.attach('#card-container');
    return card;
  };

  const onQuantityChange = (ticket: Ticket, quantityString: string) => {
    const quantity = Number(quantityString);

    if (quantityString === '-') {
      const newOrder = { ...order };
      delete newOrder[ticket.Name];
      return setOrder({ ...newOrder });
    }

    setOrder({
      ...order,
      [ticket.Name]: {
        unitPrice: ticket.Price,
        quantity,
        total: ticket.Price * quantity
      }
    });
  };

  const generateAvailableTicketOptions = (ticket: Ticket) => {
    const numberOfBookableTickets =
      ticket.Available >= 50 ? 50 : ticket.Available > 0 ? ticket.Available : 0;

    return [...Array(numberOfBookableTickets).fill(null)].map((_, index) => (
      <option key={index} value={index + 1}>
        {index + 1}
      </option>
    ));
  };

  const getOrderTotal = () => {
    if (!order) {
      return 0;
    }

    return Object.keys(order).reduce((total = 0, ticketType) => {
      return total + order[ticketType].total;
    }, 0);
  };

  const getTotalNumberOfTickets = () => {
    if (!order) {
      return 0;
    }

    return Object.keys(order).reduce((total = 0, ticketType) => {
      return total + order[ticketType].quantity;
    }, 0);
  };

  const onSubmit = async (data: Record<string, string>) => {
    if (isSubmitting) {
      return;
    }

    const {
      addressLine1,
      addressLine2,
      city,
      county,
      country,
      firstName,
      lastName,
      email,
      phone,
      teamName,
      dietaryRequirements
    } = data;

    try {
      setIsSubmitting(true);
      const token = await tokenize(card);
      const verificationToken = await verifyBuyer(payments.current, token, {
        addressLine1,
        addressLine2,
        city,
        county,
        country,
        firstName,
        lastName,
        email,
        phone,
        amount: getOrderTotal()
      });

      const order = await createOrder({
        baseId,
        date,
        payment: {
          token,
          verificationToken
        },
        order: {
          teamName,
          dietaryRequirements,
          firstName,
          lastName,
          email,
          phone,
          quantity: getTotalNumberOfTickets(),
          total: getOrderTotal()
        }
      });

      setIsOrderConfirmed(true);
    } catch (e) {
      setError('We were unable to process your order. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return <p className="text-white text-center">Loading...</p>;
  }

  if (isOrderConfirmed) {
    return (
      <div className="w-full flex items-center justify-center text-center p-10 bg-black bg-opacity-20 rounded-xl">
        <div className="text-white flex flex-col text-center items-center">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-20 w-20 opacity-40 mb-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <h4 className="text-xl text-rt-orange mb-4">You&apos;ve secured your tickets</h4>
          <p className="text-white">Please check your email for confirmation.</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <ul className="mb-10">
        {tickets.map((ticket) => (
          <li key={ticket.ID} className="grid grid-cols-4 border-b border-white pb-10 mb-10">
            <div className="col-span-3">
              <h4 className="text-lg text-white">{ticket.Name}</h4>
              <p className="text-white mb-2">
                {formatCurrency(ticket.Price)}{' '}
                <small className="text-xs text-rt-light-grey">(inc. booking fee)</small>
              </p>
              <p className="text-rt-light-grey text-sm">{ticket.Description}</p>
            </div>
            <div className="col-span-1 flex items-center">
              {ticket.Available && ticket.Available >= 0 ? (
                <Select
                  className="ml-auto w-20"
                  label={`${ticket.Name} ticket quantity`}
                  value={order ? order[ticket.Name]?.quantity.toString() : ''}
                  hideLabel
                  onChange={(evt: { target: { value: string } }) =>
                    onQuantityChange(ticket, evt.target.value)
                  }
                  name={`ticket-${ticket.ID}-quantity`}
                  id={`ticket-${ticket.ID}-quantity`}>
                  <option value="-">-</option>
                  {generateAvailableTicketOptions(ticket)}
                </Select>
              ) : (
                <span className="rounded-full p-3 px-5 ml-auto bg-red-600">Sold out</span>
              )}
            </div>
          </li>
        ))}
      </ul>

      {hasOrder ? (
        <form onSubmit={handleSubmit(onSubmit)} className="grid grid-cols-12 gap-8">
          {error ? (
            <div className="col-span-12">
              <Notification variant="error">{error}</Notification>
            </div>
          ) : null}
          <div className="col-span-12 md:col-span-7">
            <TextInput
              type="text"
              id="teamName"
              name="teamName"
              label="Team name"
              onChange={(evt) => setValue('teamName', evt.target.value)}
              placeholder="Leave blank if not yet known"
              // @ts-ignore
              register={register}
            />
            <Textarea
              id="dietaryRequirements"
              name="dietaryRequirements"
              label="If any guests have dietary requirements please let us know below"
              placeholder="e.g. vegan, allergies, etc"
              onChange={(evt) => setValue('dietaryRequirements', evt.target.value)}
              // @ts-ignore
              register={register}
            />
            <h3 className="text-xl text-rt-orange mb-4">Contact information</h3>
            <div className="grid grid-cols-2 gap-4 mb-2">
              <TextInput
                type="text"
                id="firstName"
                name="firstName"
                label="First name"
                onChange={(evt) => setValue('firstName', evt.target.value)}
                required
                error={errors.firstName && 'First name is required'}
                // @ts-ignore
                register={register}
              />
              <TextInput
                type="text"
                id="lastName"
                name="lastName"
                label="Last name"
                onChange={(evt) => setValue('lastName', evt.target.value)}
                required
                error={errors.lastName && 'Last name is required'}
                // @ts-ignore
                register={register}
              />
              <TextInput
                type="email"
                id="email"
                name="email"
                label="Email address"
                onChange={(evt) => setValue('email', evt.target.value)}
                required
                error={errors.email && 'Email address is required'}
                validation={{
                  pattern:
                    /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
                }}
                // @ts-ignore
                register={register}
              />
              <TextInput
                type="tel"
                id="phone"
                name="phone"
                label="Telephone number"
                onChange={(evt) => setValue('phone', evt.target.value)}
                required
                error={errors.phone && 'Telephone number is required'}
                // @ts-ignore
                register={register}
              />
            </div>
            <h3 className="text-xl text-rt-orange mb-4">Billing details</h3>
            <div className="grid grid-cols-2 gap-4">
              <TextInput
                type="text"
                id="addressLine1"
                name="addressLine1"
                label="Address line 1"
                onChange={(evt) => setValue('addressLine1', evt.target.value)}
                required
                error={errors.addressLine1 && 'Address line 1 is required'}
                // @ts-ignore
                register={register}
              />
              <TextInput
                type="text"
                id="addressLine2"
                name="addressLine2"
                label="Address line 2"
                onChange={(evt) => setValue('addressLine2', evt.target.value)}
                // @ts-ignore
                register={register}
              />
              <TextInput
                type="text"
                id="city"
                name="city"
                label="City"
                onChange={(evt) => setValue('city', evt.target.value)}
                required
                error={errors.city && 'City is required'}
                // @ts-ignore
                register={register}
              />
              <TextInput
                type="text"
                id="county"
                name="county"
                label="County/Region"
                onChange={(evt) => setValue('county', evt.target.value)}
                required
                error={errors.county && 'County/Region is required'}
                // @ts-ignore
                register={register}
              />
              <Select
                id="country"
                name="country"
                label="Country"
                onChange={(evt) => setValue('country', evt.target.value)}
                required
                error={errors.country && 'Country is required'}
                // @ts-ignore
                register={register}>
                <option></option>
                {countries.map(({ name, code }) => (
                  <option key={code} value={code}>
                    {name}
                  </option>
                ))}
              </Select>
              <div className="col-span-2">
                <label className="text-white mb-2 block text-sm">Payment details</label>
                <div id="card-container"></div>
              </div>
            </div>
          </div>
          <div className="col-span-12 md:col-span-5">
            <div className="sticky top-5">
              <div className="bg-black bg-opacity-20 rounded-xl p-6 mb-5">
                <h3 className="text-xl text-rt-orange mb-4">Order summary</h3>
                <ul className="mb-4">
                  {Object.keys(order).map((ticketType) => (
                    <li key={ticketType} className="flex pb-4 text-white text-sm">
                      <span>
                        {order[ticketType].quantity} ✕ {ticketType}
                      </span>
                      <span className="ml-auto">{formatCurrency(order[ticketType].total)}</span>
                    </li>
                  ))}
                  <li className="flex pt-4 text-white text-sm border-t border-white">
                    <span>Total</span>
                    <span className="ml-auto">{formatCurrency(getOrderTotal())}</span>
                  </li>
                </ul>
                <button
                  type="submit"
                  id="card-button"
                  disabled={isSubmitting}
                  className="inline-flex items-center justify-center bg-rt-orange w-full rounded-xl p-3 text-xl text-center text-white">
                  {isSubmitting ? (
                    <svg
                      className="animate-spin h-6 w-6 mr-2 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24">
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth={4}></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                  ) : (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-6 w-6 mr-2"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                      />
                    </svg>
                  )}
                  Pay now
                </button>
              </div>
              <div className="flex flex-col items-center opacity-40">
                <span className="text-white text-xs block mb-2">Payment secured by</span>
                <Image src={squareLogo} alt="Square" height={25} width={150} layout="fixed" />
              </div>
            </div>
          </div>
        </form>
      ) : null}
    </>
  );
};

export default TicketBooker;
